// kernel/agentTurnShared.ts
import { requiresListScriptPersistence } from '#lib/list/factoryReferenceLookup';
import { RefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { KernelHandle, AgentObservedError, AgentRuntimeErrorReport, Message, ScriptInstance, UiCommandInfo, AgentMode, ContextAnchor, ChatSession, ActiveEntityFocus, CommandTargetEntityType, UiCommandDefinition, SessionRuntimeState } from '#types/types';
import { COMMAND_DEFINITIONS } from '@/components/agentCommands';
import { resolveSourceFocusedBrandId } from '#lib/leasing/sourceFocusedBrand';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';

// Agent error pipeline (docs/specs/agent-error-pipeline.md): observed runtime
// errors ride into EVERY turn (director and dealer) until resolved or
// escalated. Snapshot is compact and capped; NEW entries flip to REPORTED once
// a turn carrying them is dispatched.
const MAX_RUNTIME_ERRORS_PER_TURN = 8;
/**
 * How long an unresolved error keeps riding into turns after its LAST occurrence.
 *
 * "Until resolved or escalated" assumed something eventually resolves them, and for a transient
 * client error nothing does. Observed over one long session: a single failure from hours earlier
 * rode into every subsequent turn, so the agent opened almost every reply with "I saw the refresh
 * loop again", spent turns re-checking it, and finally filed a support ticket about it entirely on
 * its own. An error nobody has seen recur in ten minutes is not happening now, and telling the
 * agent it is costs a turn every time.
 */
const RUNTIME_ERROR_STALE_AFTER_MS = 10 * 60 * 1000;
export const snapshotRuntimeErrorsForAgent = (
  kernel: { current: KernelHandle | null }
): AgentRuntimeErrorReport[] | undefined => {
  const ledger = kernel.current?.getAgentErrorLedger?.() ?? [];
  const now = Date.now();
  const active = ledger.filter(
    (entry) =>
      (entry.status === 'NEW' || entry.status === 'REPORTED') &&
      now - entry.ts <= RUNTIME_ERROR_STALE_AFTER_MS,
  );
  if (active.length === 0) return undefined;
  return active.slice(-MAX_RUNTIME_ERRORS_PER_TURN).map((entry) => ({
    id: entry.id,
    source: entry.source,
    message: entry.message,
    count: entry.count,
    ageSeconds: Math.max(0, Math.round((now - entry.ts) / 1000)),
    status: entry.status,
  }));
};
export const markRuntimeErrorsReported = (kernel: { current: KernelHandle | null }): void => {
  const ledger = kernel.current?.getAgentErrorLedger?.() ?? [];
  if (!ledger.some((entry) => entry.status === 'NEW')) return;
  kernel.current?.setAgentErrorLedger?.((prev: AgentObservedError[]) =>
    prev.map((entry) => (entry.status === 'NEW' ? { ...entry, status: 'REPORTED' as const } : entry))
  );
};
const MAX_CHAT_HISTORY_CHARS = 400 * 1024;

const MEDIA_GENERATION_COMMANDS = new Set(['GENERATE_MEDIA', 'GENERATE_VARIATIONS']);
const PLANNING_COMMANDS = new Set(['SET_PLAN']);

const normalizePromptTextForIntent = (value: string): string => (
  value.toLowerCase().replace(/\s+/g, ' ').trim()
);

const isPromptRewriteOnlyRequest = (prompt: string): boolean => {
  const normalized = normalizePromptTextForIntent(prompt);
  if (!normalized.includes('prompt')) {
    return false;
  }

  const asksToChangePrompt =
    /\b(rewrite|revise|refine|improve|update|change|edit|adjust|modify|amend)\b/.test(normalized) ||
    /\bmake\b[\s\S]{0,80}\bprompt\b/.test(normalized) ||
    /\bprompt\b[\s\S]{0,80}\b(make|mention|include|say|state|specify|add)\b/.test(normalized);

  if (!asksToChangePrompt) {
    return false;
  }

  const explicitlyForbidsExecution =
    /\b(do not|don't|dont|no need to|without)\s+(generate|run|launch|start|render|submit|send|execute)\b/.test(normalized) ||
    /\b(do not|don't|dont)\s+(use|apply)\s+(it|this|that|the\s+prompt)\b/.test(normalized);

  if (explicitlyForbidsExecution) {
    return true;
  }

  const explicitlyRequestsExecution =
    /\b(and|then|also|now)\s+(generate|run|launch|render|submit|send|execute)\b/.test(normalized) ||
    /\b(generate|run|launch|render|submit|send|execute)\s+(it|this|that|the\s+(prompt|generation|video|image)|a\s+video|an\s+image|a\s+image|media|seedance)\b/.test(normalized) ||
    /\bstart\s+(?:the\s+)?(?:generation|render|video|image|model|seedance)\b/.test(normalized) ||
    /\b(use|apply)\s+(?:it|this|that|the\s+prompt)\b/.test(normalized);

  return !explicitlyRequestsExecution;
};

const isAnswerOnlyObservationRequest = (prompt: string): boolean => {
  const normalized = normalizePromptTextForIntent(prompt);
  if (!normalized) return false;

  const asksObservation =
    /\b(describe|what happens|what is happening|what do you see|tell me what|is there|are there|visible|signs?|words?|text|read|how long|duration|what about now)\b/.test(normalized);
  if (!asksObservation) return false;

  const asksForWorkspaceAction =
    /\b(generate|create|make|change|edit|replace|remove|add|run|launch|set|plan|continue|iterate|render|apply|execute|move|resize|crop|split|trim)\b/.test(normalized);
  return !asksForWorkspaceAction;
};

export const removeMediaGenerationCommandsForPromptRewrite = (
  script: ScriptInstance | null,
  userPrompt: string,
): ScriptInstance | null => {
  if (!script || !isPromptRewriteOnlyRequest(userPrompt)) {
    return script;
  }

  const commands = script.commands.filter(command => !MEDIA_GENERATION_COMMANDS.has(command.action_type));
  if (commands.length === script.commands.length) {
    return script.autoRecall === false ? script : { ...script, autoRecall: false };
  }

  console.warn('[useAgents] Removed media generation command(s) from a prompt-rewrite-only turn.', {
    scriptId: script.id,
    removedCount: script.commands.length - commands.length,
  });

  if (commands.length === 0) {
    return null;
  }

  return { ...script, commands, autoRecall: false };
};

export const removePlanningCommandsForAnswerOnlyRequest = (
  script: ScriptInstance | null,
  userPrompt: string,
): ScriptInstance | null => {
  if (!script || !isAnswerOnlyObservationRequest(userPrompt)) {
    return script;
  }

  const commands = script.commands.filter(command => !PLANNING_COMMANDS.has(command.action_type));
  if (commands.length === script.commands.length) {
    return script.autoRecall === false ? script : { ...script, autoRecall: false };
  }

  console.warn('[useAgents] Removed planning command(s) from an answer-only observation turn.', {
    scriptId: script.id,
    removedCount: script.commands.length - commands.length,
  });

  if (commands.length === 0) {
    return null;
  }

  return { ...script, commands, autoRecall: false };
};

export const getCommandsForMode = (mode: AgentMode): UiCommandInfo[] => {
  return Object.values(COMMAND_DEFINITIONS)
    .map(def => {
      const { handler, onDisabled, ...rest } = def;
      const availableInModes = [...(def.availableInModes ?? [])];
      return { ...rest, availableInModes, targetEntityType: def.targetEntityType ?? 'ABSOLUTE' };
    })
    .filter(cmd => cmd.availableInModes.includes(mode));
};

type CommandLockContext = {
  kernel: RefObject<KernelHandle | null>;
  activeSubject: ActiveEntityFocus | null;
  anchors: ContextAnchor[];
  activeProjectId?: string | null;
};

const hasTargetContext = (
  targetEntityType: CommandTargetEntityType,
  context: CommandLockContext
): boolean => {
  if (targetEntityType === 'ABSOLUTE') return true;
  if (targetEntityType === 'Project') {
    return typeof context.activeProjectId === 'string' && context.activeProjectId.trim().length > 0;
  }

  const activeMatches =
    context.activeSubject?.entityType === targetEntityType &&
    typeof context.activeSubject.entityId === 'string' &&
    context.activeSubject.entityId.length > 0;

  if (activeMatches) return true;
  // The brand open or ticked in Source is the brand every Brand command defaults to when it runs,
  // so it satisfies the lock too. Without it /prove with Kiabi selected saw GATHER_EVIDENCE locked
  // and parameterless, and fell back to a QUERY on an entity the route refuses (2026-09-26).
  if (targetEntityType === 'Brand' && resolveSourceFocusedBrandId(context.kernel.current?.getState?.())) return true;

  return context.anchors.some(
    (anchor) =>
      anchor.entityType === targetEntityType &&
      typeof anchor.entityId === 'string' &&
      anchor.entityId.length > 0
  );
};

export const evaluateDefinitionLockState = (
  definition: UiCommandDefinition | undefined,
  context: CommandLockContext
): { isLocked: boolean; unlockHint: string } | null => {
  if (!definition) return null;

  if (definition.evaluateLockState) {
    return definition.evaluateLockState(context);
  }

  const targetEntityType = definition.targetEntityType ?? 'ABSOLUTE';
  if (targetEntityType === 'ABSOLUTE') {
    return null;
  }

  if (hasTargetContext(targetEntityType, context)) {
    return { isLocked: false, unlockHint: '' };
  }

  return {
    isLocked: true,
    unlockHint: `Focus or anchor a ${targetEntityType} first.`,
  };
};

/** A turn reads the whole history, so a message queued before it starts is answered by it: the queue is emptied. */
export const takeQueuedMessage = (k: KernelHandle, sessionId: string) => {
  if (k.readEntityRuntimeState('ChatSession', sessionId).queuedUserMessage) k.patchEntityRuntimeState('ChatSession', sessionId, { queuedUserMessage: null });
};

export const patchSessionRuntimeIfAbortControllerOwner = (
  kernelHandle: KernelHandle | null,
  sessionId: string,
  ownerAbortController: AbortController,
  patch: Partial<SessionRuntimeState>
): boolean => {
  if (!kernelHandle) return false;
  const runtime = kernelHandle.readEntityRuntimeState('ChatSession', sessionId);
  if (runtime.abortController !== ownerAbortController) {
    return false;
  }
  kernelHandle.patchEntityRuntimeState('ChatSession', sessionId, patch);
  return true;
};

export const getTruncatedChatHistory = (messages: Message[]): Message[] => {
  if (!messages || messages.length === 0) {
    return [];
  }

  const truncatedHistory: Message[] = [];
  let currentChars = 0;

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];

    if (!message) {
      continue;
    }

    const contentLength = message?.content?.length ?? 0;
    const thoughtsLength = message?.thoughts?.length ?? 0;
    let scriptLength = 0;

    if (message?.scriptsInstances?.length) {
      try {
        scriptLength = JSON.stringify(message.scriptsInstances).length;
      } catch {
        // Ignore serialization issues when estimating history size.
      }
    }

    const totalLength = contentLength + thoughtsLength + scriptLength;

    if (currentChars + totalLength > MAX_CHAT_HISTORY_CHARS) {
      if (truncatedHistory.length === 0) {
        truncatedHistory.push(message);
      }
      break;
    }

    truncatedHistory.push(message);
    currentChars += totalLength;
  }

  return truncatedHistory.reverse();
};

export const resolveMessageTargetFromSession = (
  session: ChatSession | null | undefined
): { targetEntityType?: string; targetEntityId?: string } => {
  if (!session) return {};
  const subject = isEntityFocusSubject(session.activeSubject) ? session.activeSubject : null;
  if (subject) {
    return {
      targetEntityType: subject.entityType,
      targetEntityId: subject.entityId.trim(),
    };
  }

  const fallbackProjectId = resolveProjectIdFromChatSessionContext(session);
  if (fallbackProjectId) {
    return {
      targetEntityType: 'Project',
      targetEntityId: fallbackProjectId,
    };
  }

  return {};
};

export const emitAssistantMessageEventFromSession = async (
  kernelHandle: KernelHandle | null,
  sessionId: string,
  messageTimestamp: string
): Promise<void> => {
  if (!kernelHandle || !sessionId || !messageTimestamp) return;
  const session = kernelHandle.getInventory().ChatSession.registry[sessionId];
  if (!session) return;
  const sessionMessages = kernelHandle.getChatSessionMessages(sessionId);

  const finalMessage = [...sessionMessages]
    .reverse()
    .find((message) => message.timestamp === messageTimestamp && message.role === 'assistant');

  if (!finalMessage) return;
  const persistenceUserId = kernelHandle.getUserContext().dbUserId;

  const fallbackProjectId = resolveProjectIdFromChatSessionContext(session);
  const sessionSubject = isEntityFocusSubject(session.activeSubject) ? session.activeSubject : null;
  const targetEntityType =
    finalMessage.targetEntityType ??
    sessionSubject?.entityType ??
    (fallbackProjectId ? 'Project' : null);
  const targetEntityId =
    finalMessage.targetEntityId ??
    sessionSubject?.entityId ??
    fallbackProjectId ??
    null;

  await kernelHandle.mutateInventory([{
    action: 'CREATE',
    entity: 'TimelineEvent',
    tempId: `timeline-event-agent-${uuidv4()}`,
    payload: {
      chatSessionId: sessionId,
      category: 'COMMUNICATION',
      eventType: 'ASSISTANT_MESSAGE',
      payload: finalMessage as unknown as Record<string, unknown>,
      ...(targetEntityType ? { targetEntityType } : {}),
      ...(targetEntityId ? { targetEntityId } : {}),
    },
  }], `Create assistant message timeline event for session ${sessionId}`, finalMessage.scriptsInstances?.some(requiresListScriptPersistence) ? AbortSignal.timeout(30_000) : undefined)
    .then((results) => {
      const createdEventId = results[0]?.id;
      if (!createdEventId || !kernelHandle) return;
      if (finalMessage.scriptsInstances?.some(requiresListScriptPersistence)
        && kernelHandle.getUserContext().dbUserId !== persistenceUserId) throw new Error('The account changed while saving this reference lookup.');
      void kernelHandle.syncEntity('TimelineEvent', createdEventId).catch(() => undefined);
      kernelHandle.updateActiveSessionMessages(
        (prevMessages) =>
          prevMessages.map((message) =>
            message.timestamp === messageTimestamp &&
            message.role === 'assistant' &&
            !message.timelineEventId
              ? { ...message, timelineEventId: createdEventId }
              : message
          ),
        sessionId
      );
    })
    .catch((error) => {
      if (finalMessage.scriptsInstances?.some(requiresListScriptPersistence)) throw error;
      console.error('[useAgents] Failed to persist assistant message timeline event', error);
    });
};
